/**
 * The full-size map's step C (DIRECTION §0.11, "the route graph ... the spine: convoys, bots' long walks, the ship's choice
 * and the table's routes all read it"; the walks measured and left straight — see BUILDLIST 69.8 step C): the planet's coarse route graph — the terrain mesh's own vertices (~10k on a
 * megaplanet, ~17 people apart at the human scale) joined by the mesh's edges, each edge's grade exact (the coarse ground is
 * linear between vertices). Built from the terrain alone, as generated (`initialHeightmap ?? heightmap`): no obstacles (the
 * local steer and D3's person-scale nav, core/surfaceNav.ts, keep those), so it is a pure function of the planet, built once
 * and never stale. Two ways over it: on foot and on wheels, each its own pace by grade and its own steepest edge refused.
 *
 * A trip reads a **field**: a Dijkstra search out from the destination's vertex, every vertex's cost to go and its next hop
 * (memoised per destination vertex and way; a pure function, so the memo never changes an answer). From the field any
 * number of walkers read their next waypoint and their trip's cost in O(1). Costs are flat-equivalent world units: a
 * second at a pace is the cost ÷ that pace. Craters after generation do not reroute (their bowls are person-scale).
 */
import type { GameState, PlanetState, Vec3 } from '../types';
import { ROUTE } from '../constants/action';
import { nearestSurfaceVertex } from './surface';
import { dacos } from './dmath';

export type RouteWay = 'foot' | 'wheels';

/** The graph: unique vertices (their directions and ground radii) and the edges as CSR adjacency; `nodeOf` maps a mesh position to its vertex. */
export interface PlanetRouteGraph {
  count: number;
  dirs: Float64Array;
  radii: Float64Array;
  nodeOf: Int32Array;
  start: Uint32Array;
  nbr: Uint32Array;
}

/** A trip's field toward one destination vertex: each vertex's cost to go (Infinity: no way) and its next hop (−1 at the destination or with no way). */
export interface RouteField { node: number; cost: Float64Array; next: Int32Array }

const graphs = new WeakMap<Float32Array, PlanetRouteGraph>();
const fieldMemo = new WeakMap<PlanetRouteGraph, Map<string, RouteField>>();

/** The planet's route graph (built on first ask, then cached by its mesh and generated heights). */
export function planetRouteGraph(planet: PlanetState): PlanetRouteGraph {
  const heights = planet.initialHeightmap ?? planet.heightmap;
  const hit = graphs.get(heights);
  if (hit) return hit;
  const bp = planet.basePositions, positions = bp.length / 3;
  // unique vertices: a shared vertex repeats its exact position in every triangle that holds it
  const nodeOf = new Int32Array(positions);
  const seen = new Map<string, number>();
  const firstPos: number[] = [];
  for (let i = 0; i < positions; i++) {
    const key = `${bp[3 * i]},${bp[3 * i + 1]},${bp[3 * i + 2]}`;
    let id = seen.get(key);
    if (id === undefined) { id = firstPos.length; seen.set(key, id); firstPos.push(i); }
    nodeOf[i] = id;
  }
  const count = firstPos.length;
  const dirs = new Float64Array(3 * count), radii = new Float64Array(count);
  for (let v = 0; v < count; v++) {
    const i = firstPos[v], x = bp[3 * i], y = bp[3 * i + 1], z = bp[3 * i + 2];
    const len = Math.sqrt(x * x + y * y + z * z);
    dirs[3 * v] = x / len; dirs[3 * v + 1] = y / len; dirs[3 * v + 2] = z / len;
    radii[v] = len + heights[i];
  }
  // edges: each triangle's three sides, once a pair, both ways
  const pairs = new Set<number>();
  const deg = new Uint32Array(count);
  const tris = Math.floor(positions / 3);
  const link = (a: number, b: number) => { if (a === b) return; const k = a < b ? a * count + b : b * count + a; if (pairs.has(k)) return; pairs.add(k); deg[a]++; deg[b]++; };
  for (let t = 0; t < tris; t++) { const a = nodeOf[3 * t], b = nodeOf[3 * t + 1], c = nodeOf[3 * t + 2]; link(a, b); link(b, c); link(c, a); }
  const start = new Uint32Array(count + 1);
  for (let v = 0; v < count; v++) start[v + 1] = start[v] + deg[v];
  const fill = start.slice(0, count), nbr = new Uint32Array(start[count]);
  for (const k of [...pairs].sort((p, q) => p - q)) { const a = Math.floor(k / count), b = k % count; nbr[fill[a]++] = b; nbr[fill[b]++] = a; }
  const g: PlanetRouteGraph = { count, dirs, radii, nodeOf, start, nbr };
  graphs.set(heights, g);
  return g;
}

const dirOf = (g: PlanetRouteGraph, v: number): Vec3 => ({ x: g.dirs[3 * v], y: g.dirs[3 * v + 1], z: g.dirs[3 * v + 2] });
const arcBetween = (a: Vec3, b: Vec3): number => {
  const la = Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z), lb = Math.sqrt(b.x * b.x + b.y * b.y + b.z * b.z);
  return dacos(Math.max(-1, Math.min(1, (a.x * b.x + a.y * b.y + a.z * b.z) / (la * lb))));
};

/** The cost of the edge from `a` to `b` (flat-equivalent world units), Infinity where that way refuses it. */
export function edgeCost(g: PlanetRouteGraph, a: number, b: number, way: RouteWay, planetRadius: number, roads?: Roads | null): number {
  const L0 = arcBetween(dirOf(g, a), dirOf(g, b)) * planetRadius;
  if (L0 < 1e-12) return 0;
  // (216 (4), GameConfig.roads: a road edge is cheap transit — its length counts ROUTE.roadWheels on wheels, ROUTE.roadFoot on foot)
  const L = roads && roads.edges.has(a < b ? a * g.count + b : b * g.count + a) ? L0 * (way === 'wheels' ? ROUTE.roadWheels : ROUTE.roadFoot) : L0;
  const grade = (g.radii[b] - g.radii[a]) / L0;
  if (way === 'foot') {
    if (grade > ROUTE.footMaxGrade) return Infinity;   // (a climb the walk refuses; down is always allowed, as stepWalk's)
    return L * (1 + ROUTE.footUphill * Math.max(0, grade) + ROUTE.footDownhill * Math.max(0, -grade - ROUTE.footEasyDown));
  }
  if (Math.abs(grade) > ROUTE.wheelsMaxGrade) return Infinity;
  return L * (1 + ROUTE.wheelsUphill * Math.max(0, grade));
}

/** The field toward `dest` on `way` (memoised per destination vertex). */
export function routeField(planet: PlanetState, dest: Vec3, way: RouteWay, roads?: Roads | null): RouteField {
  const g = planetRouteGraph(planet);
  const node = g.nodeOf[nearestSurfaceVertex(planet, dest)];
  let memo = fieldMemo.get(g);
  if (!memo) { memo = new Map(); fieldMemo.set(g, memo); }
  const key = `${way}:${node}:${roads?.key ?? ''}`;
  const hit = memo.get(key);
  if (hit) { memo.delete(key); memo.set(key, hit); return hit; }
  const cost = new Float64Array(g.count).fill(Infinity), next = new Int32Array(g.count).fill(-1);
  const done = new Uint8Array(g.count);
  // a binary heap of (cost, vertex), ties to the lower vertex
  const heapV: number[] = [], heapC: number[] = [];
  const less = (i: number, j: number) => heapC[i] < heapC[j] || (heapC[i] === heapC[j] && heapV[i] < heapV[j]);
  const swap = (i: number, j: number) => { const v = heapV[i], c = heapC[i]; heapV[i] = heapV[j]; heapC[i] = heapC[j]; heapV[j] = v; heapC[j] = c; };
  const push = (v: number, c: number) => {
    heapV.push(v); heapC.push(c);
    for (let i = heapV.length - 1; i > 0;) { const p = (i - 1) >> 1; if (!less(i, p)) break; swap(i, p); i = p; }
  };
  const pop = (): number => {
    const v = heapV[0], lastV = heapV.pop()!, lastC = heapC.pop()!;
    if (heapV.length > 0) {
      heapV[0] = lastV; heapC[0] = lastC;
      for (let i = 0; ;) { const l = 2 * i + 1, r = l + 1; let m = i; if (l < heapV.length && less(l, m)) m = l; if (r < heapV.length && less(r, m)) m = r; if (m === i) break; swap(i, m); i = m; }
    }
    return v;
  };
  cost[node] = 0; push(node, 0);
  while (heapV.length > 0) {
    const u = pop();
    if (done[u]) continue;
    done[u] = 1;
    for (let k = g.start[u]; k < g.start[u + 1]; k++) {
      const w = g.nbr[k];
      if (done[w]) continue;
      const c = cost[u] + edgeCost(g, w, u, way, planet.radius, roads);   // (the walk from w to u: toward the destination)
      if (c < cost[w]) { cost[w] = c; next[w] = u; push(w, c); }
    }
  }
  const field: RouteField = { node, cost, next };
  memo.set(key, field);
  while (memo.size > ROUTE.fieldMemo) memo.delete(memo.keys().next().value!);
  return field;
}

/**
 * Where a trip from `from` to `dest` on `way` heads next — the string pulled: of the next ROUTE.lookHops vertices along its
 * field, the farthest whose straight line from `from` keeps every vertex before it within ROUTE.corridor of the mesh's
 * spacing (so open ground is walked straight, and the way bends only where the ground bends it); `dest` itself once the
 * goal's vertex is in that reach, or with no way.
 */
export function routeStep(planet: PlanetState, from: Vec3, dest: Vec3, way: RouteWay, roads?: Roads | null): Vec3 {
  const g = planetRouteGraph(planet), f = routeField(planet, dest, way, roads);
  const v = g.nodeOf[nearestSurfaceVertex(planet, from)];
  if (!Number.isFinite(f.cost[v])) return dest;
  const hops: number[] = [];
  for (let w = v, k = 0; k < ROUTE.lookHops && f.next[w] >= 0; k++) { w = f.next[w]; hops.push(w); }
  if (hops.length === 0) return dest;
  const spacing = Math.sqrt(4 * Math.PI / g.count), corridor = ROUTE.corridor * spacing;
  const a = from, al = Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
  let best = hops[0];
  for (let k = 1; k < hops.length; k++) {
    const b = dirOf(g, hops[k]);
    // the great circle from `from` to this hop: each vertex before it within the corridor of it
    let nx = a.y * b.z - a.z * b.y, ny = a.z * b.x - a.x * b.z, nz = a.x * b.y - a.y * b.x;
    const nl = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (nl < 1e-12 * al) { best = hops[k]; continue; }
    nx /= nl; ny /= nl; nz /= nl;
    let inside = true;
    for (let j = 0; j < k && inside; j++) {
      const p = dirOf(g, hops[j]);
      if (Math.abs(p.x * nx + p.y * ny + p.z * nz) > corridor) inside = false;
    }
    if (!inside) break;
    best = hops[k];
  }
  return best === f.node ? dest : dirOf(g, best);
}

/** A trip's cost from `from` to `dest` on `way` (flat-equivalent world units: ÷ a pace for seconds); Infinity with no way. */
export function routeCost(planet: PlanetState, from: Vec3, dest: Vec3, way: RouteWay, roads?: Roads | null): number {
  const g = planetRouteGraph(planet), f = routeField(planet, dest, way, roads);
  const v = g.nodeOf[nearestSurfaceVertex(planet, from)];
  return arcBetween(from, dirOf(g, v)) * planet.radius + f.cost[v] + arcBetween(dirOf(g, f.node), dest) * planet.radius;
}

/** The vertices of the trip from `from` to `dest` on `way`, in order (for the table and the tests); empty with no way. */
export function routePath(planet: PlanetState, from: Vec3, dest: Vec3, way: RouteWay, roads?: Roads | null): Vec3[] {
  const g = planetRouteGraph(planet), f = routeField(planet, dest, way, roads);
  let v = g.nodeOf[nearestSurfaceVertex(planet, from)];
  if (!Number.isFinite(f.cost[v])) return [];
  const out: Vec3[] = [dirOf(g, v)];
  while (f.next[v] >= 0) { v = f.next[v]; out.push(dirOf(g, v)); }
  return out;
}

/** 216 (4): a planet's roads — the route edges they run on (keyed a·count + b, a < b), and a key for the memos. */
export interface Roads { key: string; edges: Set<number> }
const roadMemo = new WeakMap<PlanetRouteGraph, Map<string, Roads>>();

/**
 * 216 (4) (the owner: "if we have like maybe like a town or like roads or like features there could be much more gameplay
 * around like the roads allow you to transit faster"; GameConfig.roads): the roads between `ends` (the settlements and the
 * bases) — one network, the ends joined by the shortest links that join them all (a spanning tree; each to its nearest
 * left pieces apart), each link the wheels route between its ends (without roads), the edges it uses the road. Pure,
 * memoised per planet and ends; null with fewer than two ends.
 */
export function planetRoads(planet: PlanetState, ends: readonly Vec3[]): Roads | null {
  const g = planetRouteGraph(planet);
  const nodes = [...new Set(ends.map(d => g.nodeOf[nearestSurfaceVertex(planet, d)]))].sort((a, b) => a - b);
  if (nodes.length < 2) return null;
  const key = nodes.join(',');
  let memo = roadMemo.get(g);
  if (!memo) { memo = new Map(); roadMemo.set(g, memo); }
  const hit = memo.get(key);
  if (hit) return hit;
  // one network: the ends joined by the shortest links that join them all (a spanning tree by the arc, Prim's from the first)
  const pairs = new Set<string>();
  const inTree = new Set<number>([nodes[0]]);
  while (inTree.size < nodes.length) {
    let pa = -1, pb = -1, bestD = Infinity;
    for (const a of nodes) {
      if (!inTree.has(a)) continue;
      for (const b of nodes) { if (inTree.has(b)) continue; const d = arcBetween(dirOf(g, a), dirOf(g, b)); if (d < bestD) { pa = a; pb = b; bestD = d; } }
    }
    inTree.add(pb);
    pairs.add(pa < pb ? `${pa}:${pb}` : `${pb}:${pa}`);
  }
  const edges = new Set<number>();
  for (const p of [...pairs].sort()) {
    const [a, b] = p.split(':').map(Number);
    const f = routeField(planet, dirOf(g, b), 'wheels');
    if (!Number.isFinite(f.cost[a])) continue;
    for (let v = a; f.next[v] >= 0; v = f.next[v]) { const w = f.next[v]; edges.add(v < w ? v * g.count + w : w * g.count + v); }
  }
  const roads: Roads = { key: `r${key}`, edges };
  memo.set(key, roads);
  return roads;
}

/** §0.11 step B (2): one of the planet's main routes — the vertices of a path (directions, in order) on one planet, by one way. */
export interface MainRoute { planetIndex: number; way: RouteWay; points: Vec3[] }

/**
 * §0.11 step B (2) (lane F, for the dressing and the planet table): the match's **main routes** — base to base, and each base
 * to each objective (`modeState.points.sites`), on wheels, as the route graph has them (`routePath`), on the battle planet's
 * board (the board's bases; without a board, the sides' anchors) — **deduplicated where paths share vertices**: an edge
 * already on an earlier route is not repeated, so a later path contributes only its new runs (each run its own route; a
 * path wholly on earlier ones contributes nothing). Pure (the fields are memoised per destination), in the bases' and the
 * sites' order, so the list is the same for the same state. Empty with no two bases on one planet. With roads (216 (4),
 * GameConfig.roads) the caller passes each planet's (`roadsFor`: core/roads.ts `roadsOf`), and the routes follow them as a
 * drive would.
 */
export function mainRoutes(state: Pick<GameState, 'planets' | 'players'> & Partial<Pick<GameState, 'board' | 'modeState'>>, roadsFor?: (planetIndex: number) => Roads | null): MainRoute[] {
  const out: MainRoute[] = [];
  const planets = [...new Set(state.players.map(p => p.planetIndex))].sort((a, b) => a - b);
  for (const pi of planets) {
    const planet = state.planets[pi];
    if (!planet || planet.type !== 'planet') continue;
    let bases = (state.board?.bases ?? []).filter(b => b.planetIndex === pi).sort((x, y) => x.team - y.team).map(b => b.dir);
    if (bases.length < 2) {
      const sums = new Map<number, Vec3>();
      for (const p of state.players) { if (p.planetIndex !== pi) continue; const a = sums.get(p.teamIndex) ?? { x: 0, y: 0, z: 0 }; sums.set(p.teamIndex, { x: a.x + p.surfaceDir.x, y: a.y + p.surfaceDir.y, z: a.z + p.surfaceDir.z }); }
      bases = [...sums.entries()].sort((a, b) => a[0] - b[0]).map(([, v]) => { const l = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; });
    }
    if (bases.length < 2) continue;
    const sites = (state.modeState?.points?.sites ?? []).filter(s => s.planetIndex === pi).map(s => s.surfaceDir);
    const trips: Array<[Vec3, Vec3]> = [[bases[0], bases[bases.length - 1]]];
    for (const b of bases) for (const s of sites) trips.push([b, s]);
    const roads = roadsFor?.(pi) ?? null;
    const seen = new Set<string>();   // the edges laid so far, by their vertices' directions (the graph's own numbers: the same vertex, the same key)
    const key = (a: Vec3, b: Vec3) => { const ka = `${a.x},${a.y},${a.z}`, kb = `${b.x},${b.y},${b.z}`; return ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`; };
    for (const [from, to] of trips) {
      const path = routePath(planet, from, to, 'wheels', roads);
      let run: Vec3[] = [];
      const flush = () => { if (run.length >= 2) out.push({ planetIndex: pi, way: 'wheels', points: run }); run = []; };
      for (let i = 1; i < path.length; i++) {
        const k = key(path[i - 1], path[i]);
        if (seen.has(k)) { flush(); continue; }
        seen.add(k);
        if (run.length === 0) run.push(path[i - 1]);
        run.push(path[i]);
      }
      flush();
    }
  }
  return out;
}
